import { CIRCUITS } from "./circuits";
import { F1_GUIDES } from "./f1-guides";
export const SITE_ORIGIN = "https://espenlarsson.com";
export const SITE_NAME = "F1 Tracker";
export type RouteMetadata = {
  title: string;
  description: string;
  /** Display label used in BreadcrumbList JSON-LD. Omit on "/" — the root crumb is auto-added. */
  breadcrumb?: string;
  image?: string;
};

export const ROUTE_METADATA: Record<string, RouteMetadata> = {
  "/": {
    title: "F1 Live Timing, Telemetry & Race Replays | F1 Tracker",
    description:
      "Live Formula 1 race tracking with real-time driver positions, telemetry, lap times, sector times, pit strategies, and interactive track maps.",
  },
  "/drivers": {
    title: "F1 Drivers - Career Stats & Profiles | F1 Tracker",
    description:
      "Browse Formula 1 driver profiles, career statistics, head-to-head comparisons, and season performance.",
    breadcrumb: "Drivers",
  },
  "/sessions": {
    title: "F1 Race Replays, Qualifying & Practice | F1 Tracker",
    description:
      "Explore F1 race, qualifying and practice data replays with track maps and timing. Choose a session and keep results hidden until you are ready.",
    breadcrumb: "Session History",
  },
  "/analysis": {
    title: "Race Analysis - Telemetry, Tyres & Strategy | F1 Tracker",
    description:
      "In-depth Formula 1 race analysis: telemetry, tyre degradation, pit strategy, gap evolution, and sector heatmaps.",
    breadcrumb: "Analysis",
  },
  "/calendar": {
    title: "F1 Calendar - 2026 Race Schedule | F1 Tracker",
    description:
      "Full Formula 1 race calendar with countdown timers, circuit guides, and session start times in your local timezone.",
    breadcrumb: "Calendar",
  },
  "/standings": {
    title: "F1 Standings - Drivers & Constructors Championship | F1 Tracker",
    description:
      "Live Formula 1 driver and constructor championship standings, points gaps, and season trends.",
    breadcrumb: "Standings",
  },
  "/stats": {
    title: "F1 Stats - Records, Trends & Season Insights | F1 Tracker",
    description:
      "Formula 1 statistics, records, season trends, and historical comparisons across drivers and teams.",
    breadcrumb: "Stats",
  },
};


ROUTE_METADATA["/guides"] = {
  title: "F1 Guides: Live Timing, Replays & Telemetry | F1 Tracker",
  description: "Get started with F1 live timing, spoiler-free data replays and race analysis. Clear guides to the timing tower, track map, tyres and telemetry.",
  breadcrumb: "F1 guides",
};
for (const guide of F1_GUIDES) {
  ROUTE_METADATA[`/guides/${guide.slug}`] = {
    title: `${guide.title} | F1 Tracker`, description: guide.description, breadcrumb: guide.title,
  };
}

export function buildRouteJsonLd(route: string, meta: RouteMetadata) {
  const url = `${SITE_ORIGIN}${route}`;
  const crumbs = [{ "@type": "ListItem", position: 1, name: SITE_NAME, item: `${SITE_ORIGIN}/` }];
  if (route.startsWith('/guides/')) crumbs.push({ "@type": "ListItem", position: 2, name: "F1 guides", item: `${SITE_ORIGIN}/guides` });
  if (route !== '/' && meta.breadcrumb) crumbs.push({ "@type": "ListItem", position: crumbs.length + 1, name: meta.breadcrumb, item: url });
  return [
    { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs },
    { "@context": "https://schema.org", "@type": route.startsWith('/guides/') ? "Article" : "CollectionPage",
      name: meta.title, headline: meta.title, description: meta.description, url, inLanguage: "en",
      about: { "@type": "Thing", name: "Formula 1 motorsport" },
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_ORIGIN },
      ...(route.startsWith('/guides/') ? { author: { "@type": "Organization", name: SITE_NAME, url: SITE_ORIGIN }, articleSection: F1_GUIDES.find(g => route.endsWith(g.slug))?.category } : {}),
    },
    ...(route === '/' ? [{ "@context": "https://schema.org", "@type": "WebApplication", name: SITE_NAME,
      url: SITE_ORIGIN, applicationCategory: "SportsApplication", applicationSubCategory: "Formula 1 timing and telemetry",
      operatingSystem: "Web Browser", description: meta.description,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" } }] : []),
  ];
}

ROUTE_METADATA['/circuits']={title:'F1 Circuits, Session Times & Replays | F1 Tracker',description:'Explore Formula 1 circuits, find session times and browse prepared race replays without revealing results.',breadcrumb:'Circuits'};
for(const circuit of CIRCUITS) ROUTE_METADATA[`/circuits/${circuit.slug}`]={title:`${circuit.name} F1 Sessions & Replays | F1 Tracker`,description:`Explore ${circuit.name}, ${circuit.country}: the circuit map, local session times and prepared Formula 1 data replays. Results stay hidden in the overview.`,breadcrumb:circuit.name,image:`${SITE_ORIGIN}/circuits/${circuit.slug}.png`};
